import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import AdminCard from "./AdminCard";

export default function FaqAdmin() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ question: "", answer: "", order: 0 });
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const resetForm = () => { setForm({ question: "", answer: "", order: 0 }); setEditingId(null); };
    const load = () => api.get("/faqs").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...form, order: Number(form.order) || 0 };
            if (editingId) {
                await api.put(`/faqs/${editingId}`, payload);
                toast.success("FAQ zaktualizowane.");
            } else {
                await api.post("/faqs", payload);
                toast.success("Pytanie dodane.");
            }
            resetForm();
            await load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    const startEdit = (it) => { setForm({ question: it.question, answer: it.answer, order: it.order || 0 }); setEditingId(it.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
    const del = async (id) => {
        if (!window.confirm("Usunąć to pytanie?")) return;
        await api.delete(`/faqs/${id}`);
        if (editingId === id) resetForm();
        await load();
    };

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-5">{editingId ? "Edycja FAQ" : "Nowe pytanie FAQ"}</h3>
                <form onSubmit={submit} className="space-y-4" data-testid="admin-faq-form">
                    <div className="space-y-2"><Label>Pytanie *</Label><Input required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="admin-faq-question" /></div>
                    <div className="space-y-2"><Label>Odpowiedź *</Label><Textarea required rows={5} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="admin-faq-answer" /></div>
                    <div className="space-y-2"><Label>Kolejność wyświetlania</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} placeholder="0, 1, 2..." data-testid="admin-faq-order" /></div>
                    <div className="flex items-center gap-3">
                        <Button type="submit" disabled={loading} data-testid="admin-faq-submit" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                            {loading ? "Zapisywanie..." : (editingId ? "Zapisz zmiany" : "Dodaj pytanie")}
                        </Button>
                        {editingId && <Button type="button" variant="outline" onClick={resetForm} data-testid="admin-faq-cancel" className="rounded-full">Anuluj</Button>}
                    </div>
                </form>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-5">Lista FAQ ({items.length})</h3>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {items.length === 0 && <p className="text-gray-500">Brak pytań.</p>}
                    {items.map((f) => (
                        <div key={f.id} className={`p-4 rounded-2xl bg-[#FAF8F3] border ${editingId === f.id ? "border-[#D4A537]" : "border-gray-100"}`} data-testid={`admin-faq-item-${f.id}`}>
                            <div className="flex justify-between items-start gap-3 mb-2">
                                <span className="text-[10px] uppercase tracking-wider font-semibold text-[#D4A537]">#{f.order}</span>
                                <div className="flex gap-1">
                                    <button onClick={() => startEdit(f)} className="text-[#0D4F3C] p-1.5 rounded-lg hover:bg-white" data-testid={`admin-faq-edit-${f.id}`}><Pencil size={14} /></button>
                                    <button onClick={() => del(f.id)} className="text-red-500 p-1.5 rounded-lg hover:bg-white" data-testid={`admin-faq-delete-${f.id}`}><Trash2 size={14} /></button>
                                </div>
                            </div>
                            <p className="font-serif text-base text-[#092E22] mb-1">{f.question}</p>
                            <p className="text-xs text-gray-600 line-clamp-3">{f.answer}</p>
                        </div>
                    ))}
                </div>
            </AdminCard>
        </div>
    );
}
